import ComponentToggleSignal from '@/components/component-toggle-signal';
import DeliverySelector from '@/experiments/product-select-v3/DeliverySelector';
import Modal from '@/components/Modal';
import ProductOptionSelector from '@/experiments/product-select-v3/ProductOptionSelector';
import ProductSelectController from '@/components/buy-stack/ProductSelectController';
import ProgressBar from '@/experiments/product-select-v3/ProgressBar';
import RatingWidget from '@/components/RatingWidget';
import Settings from '@/components/buy-stack/ProductSelectSettings';
import SteppedButtonGroup from '@/components/buy-stack/SteppedButtonGroup';
import backArrowIcon from '@/icons/back-arrow-icon.svg';
import {logError} from '@/utilities/log';
import classNames from 'classnames';
import {createSrcSet} from '@/utilities/create-image-src-set';
import deliveryIcon from '@/icons/delivery-icon.svg';
import {formatColorName} from '@/settings/product-option-settings';
import {getColorStyle} from '@/utilities/color-style-map';
import {getCart} from '@/cart/cart-store';
import {useLocale} from '@/localization/use-locale';

/**
 * A section for selecting variant options and delivery options for a product.
 * Its requires a [ProductSelectController] to manage state and handle selection events.
 * It presents a stepped checkout flow to the customer using [ProductOptionSelector] and [DeliverySelector].
 * It is configurable using [ProductSelectSectionSettings].
 */
export default function ProductSelectSection() {
  const modalController = new ComponentToggleSignal();
  const cart = getCart();
  const currency = cart?.currency ?? 'USD';
  const locale = useLocale();

  return (
    <ProductSelectController.Consumer>
      {(controller) => (
        <Settings.Consumer>
          {({
            next_button_labels,
            product_title_override,
            shipping_guarantee,
            starting_at_label,
          }) => {
            const {alt, url} = controller.image;
            const next_button_label =
              next_button_labels[controller.step - 1] ??
              next_button_labels[next_button_labels.length - 1];

            const product = controller.product;
            const title = product_title_override ?? product?.title ?? '';
            const numberOfLoads = product?.variants[0].number_of_loads ?? 0;
            const numberOfLoadsLabel =
              numberOfLoads > 1
                ? `${numberOfLoads} loads`
                : `${numberOfLoads} load`;
            const numberOfReviews = product?.number_of_reviews ?? 0;

            const selectedOptionSettings = controller.selectedOptionSettings;
            const color = formatColorName(
              selectedOptionSettings?.color ?? 'logo-lime'
            );
            const colorStyle = getColorStyle(color);

            const handleNextStep = () => {
              controller.nextStep().catch(logError);
            };
            const handlePreviousStep = () => controller.previousStep();

            const buttonGroup = (showPreviousButton: boolean) => (
              <SteppedButtonGroup
                label={next_button_label}
                onNextStep={handleNextStep}
                onPreviousStep={handlePreviousStep}
                previousIcon={backArrowIcon}
                nextIcon={null}
                previousClassName="btn btn-square btn-neutral h-full w-16 border-none bg-bright-mist-gray"
                nextClassName={classNames(
                  'btn btn-square w-full',
                  colorStyle.button
                )}
                showPreviousButton={showPreviousButton}
              />
            );

            const shippingBlock = (
              <span className="flex items-center justify-center gap-x-2 text-sm text-neutral-dark-gray">
                <img
                  alt="Delivery icon"
                  height="24"
                  src={deliveryIcon}
                  width="24"
                />
                {shipping_guarantee}
              </span>
            );

            return (
              <>
                <section className="flex max-w-full items-start justify-center py-3 text-base md:py-0">
                  <div className="hidden max-w-sm md:block">
                    <img
                      alt={alt}
                      className="aspect-16/25 h-136 w-full object-contain"
                      onClick={() => modalController.open()}
                      sizes="(min-width: 48rem) 50vw, (min-width: 56rem) 28rem, 0"
                      src={url}
                      srcSet={createSrcSet(url, 600)}
                    />
                  </div>
                  <div className="grid w-max max-w-md grow basis-0 grid-cols-1 justify-items-center gap-y-1.5 text-center md:px-6 md:py-3">
                    <h5 className="flex flex-wrap justify-center gap-x-1.5 font-semibold uppercase tracking-normal">
                      <span className="whitespace-break-spaces">
                        {title}
                        {!!numberOfLoads && (
                          <span className="font-normal">
                            {' '}
                            {numberOfLoadsLabel}
                          </span>
                        )}
                      </span>
                    </h5>
                    {starting_at_label && (
                      <p className="rounded-full bg-gradient-to-br from-primary-teal to-primary-green px-4 pb-1 pt-0.5 text-lg font-medium text-bright-white">
                        {starting_at_label}
                      </p>
                    )}
                    <div className="flex gap-x-1">
                      <RatingWidget
                        rating={product?.average_rating ?? 0}
                        showText={false}
                        size="1.25rem"
                        totalReviews={numberOfReviews}
                      />
                      <span>
                        {Intl.NumberFormat(locale).format(numberOfReviews)}
                      </span>
                    </div>
                    <ProgressBar
                      currentStep={controller.step}
                      stepsCount={controller.stepsCount}
                    />
                    <div className="grid w-full">
                      <div
                        className={classNames(
                          'fast-transition col-start-1 row-start-1 flex flex-col gap-y-4',
                          {
                            'pointer-events-none opacity-0':
                              controller.showDeliveryOptions,
                            'opacity-1': controller.showVariantOptions,
                          }
                        )}
                      >
                        {controller.variantOptions.length > 0 && (
                          <ProductOptionSelector
                            onUpdate={(option) =>
                              controller.selectVariantOption(option)
                            }
                            options={controller.variantOptions}
                            title={controller.currentOptionName}
                            value={controller.selectedVariantOption}
                          />
                        )}
                        {buttonGroup(controller.step > 1)}
                        {shipping_guarantee && shippingBlock}
                      </div>
                      <div
                        className={classNames(
                          'fast-transition col-start-1 row-start-1 flex flex-col gap-y-4',
                          {
                            'pointer-events-none opacity-0':
                              controller.showVariantOptions,
                            'opacity-1': controller.showDeliveryOptions,
                          }
                        )}
                      >
                        <DeliverySelector
                          currency={currency}
                          locale={locale}
                          color={color}
                          deliveryOptions={controller.deliveryOptions}
                          value={controller.selectedDeliveryOption ?? undefined}
                          onUpdate={(option) =>
                            controller.selectDeliveryOption(option)
                          }
                        />
                        {buttonGroup(true)}
                        {shipping_guarantee && shippingBlock}
                      </div>
                    </div>
                  </div>
                </section>
                <Modal controller={modalController}>
                  <div className="flex h-full items-center">
                    <img
                      src={url}
                      alt={alt}
                      className="fit-inside-modal"
                      srcSet={createSrcSet(url, 600)}
                      loading="lazy"
                    />
                  </div>
                </Modal>
              </>
            );
          }}
        </Settings.Consumer>
      )}
    </ProductSelectController.Consumer>
  );
}
